import type { AccessPayload, Question, QuestionResult } from '../types'

const PREFIX = 'history-session-progress-v1'
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

export interface StoredSessionProgress {
  version: 1
  studentId: string
  assignmentId: string
  questionIds: string[]
  currentIndex: number
  questionResults: QuestionResult[]
  streak: number
  elapsedSeconds: number
  updatedAt: string
}

function storageKey(access: AccessPayload): string {
  return `${PREFIX}:${access.student.id}:${access.session.id}`
}

export function readSessionProgress(access: AccessPayload, questions: Question[]): StoredSessionProgress | null {
  try {
    const raw = localStorage.getItem(storageKey(access))
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredSessionProgress
    const updatedAt = Date.parse(parsed.updatedAt)
    const validQuestions = new Set(questions.map((question) => question.id))
    const valid = parsed.version === 1
      && parsed.studentId === access.student.id
      && parsed.assignmentId === access.session.id
      && parsed.questionIds.length > 0
      && parsed.questionIds.every((id) => validQuestions.has(id))
      && parsed.currentIndex >= 0
      && parsed.currentIndex < parsed.questionIds.length
      && Number.isFinite(updatedAt)
      && Date.now() - updatedAt <= MAX_AGE_MS
    if (!valid) {
      localStorage.removeItem(storageKey(access))
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function saveSessionProgress(access: AccessPayload, progress: Omit<StoredSessionProgress, 'version' | 'studentId' | 'assignmentId' | 'updatedAt'>): StoredSessionProgress {
  const value: StoredSessionProgress = {
    version: 1,
    studentId: access.student.id,
    assignmentId: access.session.id,
    updatedAt: new Date().toISOString(),
    ...progress,
  }
  localStorage.setItem(storageKey(access), JSON.stringify(value))
  return value
}

export function clearSessionProgress(access: AccessPayload): void {
  localStorage.removeItem(storageKey(access))
}
